import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUpRight, FileClock, LockKeyhole, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { ErrorMessage, Loading } from "../components/Loading";
import { useApi } from "../hooks/useApi";
import { canViewActivityLog } from "../lib/access";
import { api } from "../lib/api";

const actionLabels = {
  account_activated: "Account",
  account_access_changed: "Access",
  profile_claimed: "Claim",
  password_reset_requested: "Recovery",
  password_reset_issued: "Reset",
  profile_updated: "Profile",
  portrait_uploaded: "Upload",
  portrait_cropped: "Crop",
  memorial_memory_added: "Memory",
  memorial_memory_deleted: "Deletion",
  story_published: "Story",
  story_comment_added: "Comment",
  story_reaction_added: "Reaction",
  story_reaction_changed: "Reaction",
  story_reaction_removed: "Reaction",
  college_photo_curated: "Curation",
  college_photos_uploaded: "Upload",
  college_photo_updated: "Edit",
  college_comment_added: "Comment",
  college_comment_deleted: "Deletion",
  college_photo_deleted: "Deletion",
  reunion_photo_deleted: "Deletion",
  reunion_created: "Reunion",
  reunion_updated: "Edit",
  reunion_editors_updated: "Access",
  reunion_photo_updated: "Edit",
  album_photo_updated: "Edit",
  reunion_photos_uploaded: "Upload",
  reunion_comment_added: "Comment",
  reunion_comment_updated: "Comment",
  reunion_comment_deleted: "Deletion",
};

function parsedDate(value) {
  return new Date(value.endsWith("Z") ? value : `${value.replace(" ", "T")}Z`);
}

function dayKey(value) {
  const date = parsedDate(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-CA");
}

function dayLabel(value) {
  const date = parsedDate(value);
  if (Number.isNaN(date.getTime())) return "Earlier activity";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function timeLabel(value) {
  const date = parsedDate(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function AccessDenied() {
  return (
    <section className="activity-denied section-wrap">
      <LockKeyhole aria-hidden="true" />
      <p className="kicker">Private audit record</p>
      <h1>This audit trail is restricted.</h1>
      <p>Site activity is available only to Manoj Thomas.</p>
      <Link className="button button--ink" to="/members">Return to the member room</Link>
    </section>
  );
}

export default function ActivityLog({ session }) {
  const canViewActivity = session?.authenticated && canViewActivityLog(session?.user);
  const loader = useCallback(
    () => canViewActivity ? api("/api/admin/activity") : Promise.resolve({ entries: [], nextCursor: null }),
    [canViewActivity],
  );
  const { data, error, loading } = useApi(loader, [loader]);
  const [entries, setEntries] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState("");

  useEffect(() => {
    setEntries(data?.entries || []);
    setNextCursor(data?.nextCursor || null);
  }, [data]);

  const groups = useMemo(() => entries.reduce((result, entry) => {
    const key = dayKey(entry.createdAt);
    const last = result[result.length - 1];
    if (last?.key === key) last.entries.push(entry);
    else result.push({ key, label: dayLabel(entry.createdAt), entries: [entry] });
    return result;
  }, []), [entries]);

  async function loadOlder() {
    if (!nextCursor || loadingOlder) return;
    setLoadingOlder(true);
    setOlderError("");
    try {
      const result = await api(`/api/admin/activity?before=${encodeURIComponent(nextCursor)}`);
      setEntries((current) => [...current, ...(result.entries || [])]);
      setNextCursor(result.nextCursor || null);
    } catch (requestError) {
      setOlderError(requestError.message);
    } finally {
      setLoadingOlder(false);
    }
  }

  if (!session) return <Loading label="Checking administrator access" />;
  if (!canViewActivity) return <AccessDenied />;

  return (
    <>
      <section className="activity-hero">
        <div className="section-wrap activity-hero__inner">
          <div>
            <p className="kicker">Administrator record / append only</p>
            <h1>Every change,<br /><em>one clear line.</em></h1>
          </div>
          <div className="activity-hero__note">
            <FileClock aria-hidden="true" />
            <p>Uploads, deletions, profile edits, comments, reactions, reunion changes, and account access are recorded here from this feature onward.</p>
            <span><ShieldCheck aria-hidden="true" /> Manoj Thomas only</span>
          </div>
        </div>
      </section>

      <section className="activity-ledger section-wrap" aria-labelledby="activity-ledger-title">
        <header className="activity-ledger__header">
          <div>
            <p className="kicker">Audit trail</p>
            <h2 id="activity-ledger-title">Site activity</h2>
          </div>
          <p>{entries.length} change{entries.length === 1 ? "" : "s"} shown</p>
        </header>

        {loading && <Loading label="Opening the change log" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!loading && !error && groups.length === 0 && (
          <div className="activity-empty">
            <FileClock aria-hidden="true" />
            <h3>No changes recorded yet.</h3>
            <p>The first content update after this feature is enabled will appear here automatically.</p>
          </div>
        )}

        {groups.map((group) => (
          <section className="activity-day" key={group.key} aria-labelledby={`activity-day-${group.key}`}>
            <h3 id={`activity-day-${group.key}`}>{group.label}</h3>
            <div className="activity-list">
              {group.entries.map((entry) => (
                <article className="activity-entry" key={entry.id}>
                  <time dateTime={entry.createdAt}>{timeLabel(entry.createdAt)}</time>
                  <span className={`activity-entry__action is-${entry.action}`}>{actionLabels[entry.action] || "Change"}</span>
                  <div className="activity-entry__copy">
                    <p><strong>{entry.actorName}</strong> {entry.summary}.</p>
                    <span>{entry.actorRole === "administrator" ? "Administrator" : "Classmate"} · Record #{String(entry.id).padStart(4, "0")}</span>
                  </div>
                  {entry.path && (
                    <Link className="activity-entry__link" to={entry.path} aria-label={`Open the page for activity record ${entry.id}`}>
                      Open page <ArrowUpRight aria-hidden="true" />
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </section>
        ))}

        {olderError && <ErrorMessage>{olderError}</ErrorMessage>}
        {nextCursor && (
          <div className="activity-ledger__more">
            <button className="button button--ghost" type="button" onClick={loadOlder} disabled={loadingOlder}>
              {loadingOlder ? "Loading…" : "Load older changes"}
            </button>
          </div>
        )}
      </section>
    </>
  );
}
